import React from "react";
import { Link } from "react-router-dom";
import { Flame, Star } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { useLang } from "../contexts/LanguageContext";

const Heat = ({ level }) => {
  if (!level) return null;
  return (
    <div className="flex gap-0.5" aria-label={`Heat ${level} of 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Flame key={i} className={`w-3 h-3 ${i < level ? "text-[#F97316] fill-[#F97316]" : "text-foreground/15"}`} />
      ))}
    </div>
  );
};

const ProductCard = ({ product, flagship: flagshipProp }) => {
  const { add } = useCart();
  const { t } = useLang();
  const isFlagship = flagshipProp ?? product.flagship;
  const recommended = product.recommended_for || [];

  return (
    <div
      data-testid={`product-card-${product.id}`}
      className="group flex flex-col"
    >
      <Link to={`/products/${product.id}`} data-testid={`product-link-${product.id}`} className="block">
        <div className="relative overflow-hidden aspect-[3/4] bg-[#F5EFE6] flex items-center justify-center p-3 sm:p-4">
          <img
            src={product.image}
            alt={product.name}
            className="max-w-full max-h-full object-contain transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
            loading="lazy"
          />
          {isFlagship && (
            <div data-testid={`flagship-${product.id}`} className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-[#222] text-[#FFF8F0] text-[10px] font-medium tracking-[0.18em] uppercase px-3 py-1.5">
              <Star className="w-3 h-3 fill-[#F97316] text-[#F97316]" /> {t.productsSection.flagship}
            </div>
          )}
          <div
            data-testid={`coming-soon-${product.id}`}
            className="absolute top-3 right-3 bg-[#FFF8F0]/85 backdrop-blur-sm border border-[#F97316]/40 text-[#F97316] text-[10px] font-medium tracking-[0.22em] uppercase px-3 py-1.5 rounded-full"
          >
            Coming Soon
          </div>
        </div>
      </Link>

      <div className="pt-6 sm:pt-8 flex-1 flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <span className="eyebrow">{product.size}</span>
          <Heat level={product.heat} />
        </div>
        <Link to={`/products/${product.id}`} className="display-serif text-2xl sm:text-3xl leading-tight hover:text-[#F97316] transition-colors">
          {product.name}
        </Link>
        <p className="text-sm text-foreground/55 font-light mt-3 leading-relaxed line-clamp-3 flex-1">
          {product.short_description || product.description}
        </p>

        {recommended.length > 0 && (
          <div className="mt-5">
            <div className="text-[10px] tracking-[0.22em] uppercase text-foreground/45 mb-2">{t.productsSection.recommendedFor}</div>
            <div className="flex flex-wrap gap-1.5">
              {recommended.slice(0, 5).map((r) => (
                <span key={r} className="text-[11px] tracking-wide border border-border px-2 py-0.5 text-foreground/70">{r}</span>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 flex items-center justify-end border-t border-border pt-5">
          <button
            data-testid={`add-to-cart-${product.id}`}
            onClick={() => add(product)}
            className="text-xs font-medium tracking-[0.2em] uppercase text-foreground hover:text-[#F97316] transition-colors"
          >
            {t.common.addToCart} —
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
